/**
 * Searchable dropdown that lets the user pick a question (or hidden var) by
 * label. Each option shows the question's type as a small tag.
 */
import React, { useMemo } from "react"
import { Select, Tag } from "antd"
import { LabelItem, LabelType } from "utils/labelUtils"
import {
  QuestionType,
  QUESTION_TYPE_LABELS,
  isReservedWord,
} from "utils/logicWizard/grammar"
import { QuestionInfo } from "utils/logicWizard/questionTypeDetector"

interface QuestionPickerProps {
  /** All labels parsed from the questionnaire. */
  labels: LabelItem[]
  /** Question type info indexed by question label name. */
  questionInfo: Map<string, QuestionInfo>
  /** Currently selected label name (or empty string). */
  value: string
  /** Called when the user selects a question. */
  onChange: (label: string) => void
  /** Optional placeholder text. */
  placeholder?: string
  style?: React.CSSProperties
  size?: "small" | "middle" | "large"
}

const TYPE_TAG_COLOR: Record<QuestionType, string> = {
  [QuestionType.FILL_IN_NUMBER]: "blue",
  [QuestionType.FILL_IN_REAL]: "blue",
  [QuestionType.FILL_IN_TEXT]: "default",
  [QuestionType.SELECT_ONE]: "green",
  [QuestionType.SELECT_MULTIPLE]: "purple",
  [QuestionType.SELECT_RANGE]: "orange",
  [QuestionType.GRID]: "magenta",
  [QuestionType.HIDDEN]: "gold",
  [QuestionType.UNKNOWN]: "default",
}

const QuestionPicker: React.FC<QuestionPickerProps> = ({
  labels,
  questionInfo,
  value,
  onChange,
  placeholder = "Select a question...",
  style,
  size = "middle",
}) => {
  const options = useMemo(() => {
    return labels
      .filter((l) => l.type === LabelType.QUESTION)
      .filter((l) => !isReservedWord(l.name))
      .map((l) => {
        const info = questionInfo.get(l.name)
        const qtype = info?.type ?? QuestionType.UNKNOWN
        return {
          value: l.name,
          label: l.name,
          qtype,
          searchText: `${l.name} ${l.content}`.toLowerCase(),
        }
      })
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [labels, questionInfo])

  return (
    <Select
      showSearch
      value={value || undefined}
      onChange={onChange}
      placeholder={placeholder}
      style={{ minWidth: 220, ...style }}
      size={size}
      // The dropdown shows the full JSX (name + type tag); the closed control
      // shows just `value` (the question label) — keeps the closed selector
      // tidy in narrow widths.
      optionLabelProp="value"
      filterOption={(input, option) => {
        const opt = options.find((o) => o.value === option?.value)
        return opt ? opt.searchText.includes(input.toLowerCase()) : false
      }}
      options={options.map((opt) => ({
        value: opt.value,
        label: (
          <div className="flex items-center gap-2 justify-between">
            <span className="font-medium">{opt.label}</span>
            <Tag
              color={TYPE_TAG_COLOR[opt.qtype]}
              style={{ marginInlineEnd: 0, fontSize: 11 }}
            >
              {QUESTION_TYPE_LABELS[opt.qtype]}
            </Tag>
          </div>
        ),
      }))}
    />
  )
}

export default QuestionPicker
